import {
	Box,
	Button,
	Divider,
	Grid,
	Group,
	ScrollArea,
	Skeleton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPencilPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { NoDataFoundMessage } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { SearchField } from '../../components/SearchField';
import { TablePageSelector } from '../../components/TablePageSelector';
import { usePagination } from '../../hooks/usePagination';
import { useUrlParams } from '../../hooks/useUrlParams';
import { defaultIconStyle } from '../../theme/styling/iconStyling';
import { query } from '../../util/api/query';
import { NewRearDisplayModal } from './components/NewRearDisplayModal';
import { RearDisplayRow } from './components/RearDisplayRow';
import type { RearDisplayDto } from './types/RearDisplayDto';

import { Paper } from '../../components/Paper';
import { useResponsiveSpace } from '../../hooks/useResponsiveSpace';

export function RearDisplay() {
	const { organisation } = useParams();
	const pagination = usePagination(10);
	const [searchQuery, setSearchQuery] = useState('');

	const recordsParams = useUrlParams({
		searchQuery: searchQuery,
		size: pagination.value.pageSize,
		number: pagination.value.pageNumber,
	});

	const rearDisplayQuery = query<RearDisplayDto[]>(
		`${organisation}/rear-display/all?${recordsParams}`,
	);

	const countParams = useUrlParams({
		searchQuery: searchQuery,
	});

	const rearDisplayCountQuery = query<number>(
		`${organisation}/rear-display/count?${countParams}`,
	);

	const [showNewModal, { open: openNew, close: closeNew }] =
		useDisclosure(false);

	const responsiveHeight = useResponsiveSpace([
		[753, '71vh'],
		[1048, '78vh'],
	]);

	return (
		<>
			<NewRearDisplayModal
				isOpened={showNewModal}
				close={closeNew}
				triggerListUpdate={() => {
					rearDisplayQuery.remove();
					rearDisplayCountQuery.remove();
				}}
			/>
			<PageHeader title={'Rear Display'} hideFiltering>
				<Group>
					<SearchField
						placeholder={'Search file names or descriptions...'}
						onDelayComplete={(value) => setSearchQuery(value)}
					/>
					<Button
						variant={'filled'}
						color={'medepos.1'}
						autoContrast
						px={'xs'}
						onClick={() => openNew()}
					>
						<IconPencilPlus {...defaultIconStyle} />
					</Button>
				</Group>
			</PageHeader>
			<Paper>
				<ScrollArea
					h={responsiveHeight}
					w={'100%'}
					scrollHideDelay={500}
					scrollbars={'y'}
					pb={25}
				>
					{!rearDisplayQuery.isLoading ? (
						rearDisplayQuery.data?.value.map((advert) => (
							<RearDisplayRow
								key={advert.id}
								initialAdvert={advert}
								refreshTable={() => {
									rearDisplayQuery.remove();
									rearDisplayCountQuery.remove();

									rearDisplayQuery.refetch();
									rearDisplayCountQuery.refetch();
								}}
							/>
						))
					) : (
						<LoadingRows />
					)}
					{rearDisplayQuery.data && rearDisplayQuery.data?.value.length < 1 && (
						<NoDataFoundMessage size={'md'} />
					)}
				</ScrollArea>
				<Group w={'100%'} justify="flex-end">
					<TablePageSelector
						pageNumber={pagination.value.pageNumber}
						setPageNumber={pagination.setPageNumber}
						totalPages={Math.ceil(
							(rearDisplayCountQuery.data?.value ?? 1000) / 10,
						)}
						isLoading={rearDisplayCountQuery.isLoading}
						margin={'0'}
					/>
				</Group>
			</Paper>
		</>
	);
}

function LoadingRows() {
	return (
		<>
			{Array.from({ length: 10 }).map((_) => (
				<>
					<Box py={20} px={20} mb={10}>
						<Grid w="100%">
							<Skeleton h="13vh" w="100%" />
						</Grid>
					</Box>
					<Divider />
				</>
			))}
		</>
	);
}
